import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { Box, Chip, Typography } from "@mui/material";
import CustomMultiSelectAutoComplete from "../common/CustomMultiSelectAutoComplete";
import TextInputField from "../common/TextInputField";
import PlusIcon from "../../svg/PlusIcon";
import React, { useState } from "react";
import CustomTooltip from "../common/CustomTooltip";
import InfoIcon from "../../svg/InfoIcon";
import styled from "styled-components";
import { useAuth } from "../../../../providers/AuthProvider";
import { useAppStore } from "../../../../providers/AppStore";
import { ServiceDetails } from "./types";

type Props = {
  serviceDetails: ServiceDetails;
  setServiceDetails: (
    value: ServiceDetails | ((prev: ServiceDetails) => ServiceDetails)
  ) => void;
};

const BenefitsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr; /* default: 1 column */
  gap: 16px;

  @media (min-width: 500px) {
    grid-template-columns: repeat(2, 1fr); /* up to 2 columns */
  }
`;

const ServiceBenefitsWidget = ({
  serviceDetails,
  setServiceDetails,
}: Props) => {
  const { AuthUser } = useAuth();
  const { ASSetOpenSnackBar, ASSetSnackBarMessage, ASSetTypeSnackBar } =
    useAppStore();
  const theme: Theme = useTheme();
  const [editingCustomBenefits, setEditingCustomBenefits] = useState("");

  return (
    <>
      {serviceDetails.metrics.length > 0 && (
        <Box>
          <Box sx={styles.title(theme)}>PNOE benefits</Box>
          <BenefitsGrid>
            {serviceDetails.metrics.map((metric: any, index: number) => (
              <Box key={index} sx={styles.pnoeBenefitsContainer(theme)}>
                <Typography sx={styles.benefitLabel(theme)}>
                  {metric.metric ?? "Null value"}
                </Typography>
                <CustomTooltip message={metric.benefit} customHeight="auto">
                  <InfoIcon />
                </CustomTooltip>
              </Box>
            ))}
          </BenefitsGrid>
        </Box>
      )}
      <Box>
        <Box sx={styles.title(theme)}>Additional benefits</Box>
        {serviceDetails.additionalBenefits.length > 0 && (
          <>
            <Box sx={styles.subtitle(theme)}>Predefined</Box>
            <CustomMultiSelectAutoComplete
              options={serviceDetails.additionalBenefits}
              internalValue={serviceDetails.additionalBenefits.filter(
                (metric: any) => metric.active === 1
              )}
              setInternalValue={(selectedOptions: any[]) => {
                const selectedMetrics = selectedOptions.map((option: any) =>
                  typeof option === "string" ? option : option.metric
                );

                setServiceDetails((prev: any) => ({
                  ...prev,
                  additionalBenefits: prev.additionalBenefits.map(
                    (benefit: any) => ({
                      ...benefit,
                      active: selectedMetrics.includes(benefit.metric) ? 1 : 0,
                    })
                  ),
                }));
              }}
              showTooltip={true}
            />
          </>
        )}
        <Box sx={styles.separator} />
        <Box sx={styles.subtitle(theme)}>Custom</Box>
        <Box sx={styles.addCustomBenefitContainer}>
          <Box sx={{ flex: 1 }}>
            <TextInputField
              placeholder="Type a custom additional benefit"
              value={editingCustomBenefits}
              handleChange={(e) => setEditingCustomBenefits(e.target.value)}
              name="textInput"
              showClearButton={true}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              cursor:
                editingCustomBenefits.trim().length > 0 ? "pointer" : "default",
              borderRadius: "8px",
              border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
            }}
            onClick={() => {
              if (editingCustomBenefits.trim().length > 0) {
                // Check for duplicates
                const isDuplicate = serviceDetails.userAdditionalBenefits.some(
                  (benefit: any) =>
                    benefit.benefit.toLowerCase().trim() ===
                    editingCustomBenefits.toLowerCase().trim()
                );

                if (isDuplicate) {
                  ASSetSnackBarMessage({
                    title: "Duplicate Benefit",
                    message:
                      "This benefit already exists. Please enter a different benefit.",
                  });
                  ASSetTypeSnackBar("alert");
                  ASSetOpenSnackBar(true);
                  return;
                }

                const newBenefit = {
                  benefit: editingCustomBenefits,
                  id: null,
                  userID: AuthUser.pnoe_user_id,
                  userServiceID: null,
                };

                setServiceDetails((prev: any) => ({
                  ...prev,
                  userAdditionalBenefits: [
                    ...prev.userAdditionalBenefits,
                    newBenefit,
                  ],
                }));
                setEditingCustomBenefits("");
              }
            }}
          >
            <PlusIcon
              fill={
                editingCustomBenefits.trim().length > 0
                  ? theme.colors_ds.PRIMARY_ICON_MAIN
                  : theme.colors_ds.NEUTRAL_ICON_WEAK
              }
            />
          </Box>
        </Box>
        <Box sx={styles.chipContainer}>
          {serviceDetails.userAdditionalBenefits
            .filter(
              (benefit: any) => benefit.benefit && benefit.benefit.trim() !== ""
            )
            .map((benefit: any, index: number) => (
              <Chip
                key={index}
                label={benefit.benefit}
                onDelete={() => {
                  setServiceDetails((prev: any) => ({
                    ...prev,
                    userAdditionalBenefits: prev.userAdditionalBenefits.filter(
                      (item: any) => item.benefit !== benefit.benefit
                    ),
                  }));
                }}
                sx={styles.chipLabel(theme)}
              />
            ))}
        </Box>
      </Box>
    </>
  );
};

export default ServiceBenefitsWidget;

const styles = {
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    flex: 1,
    marginBottom: "16px",
  }),
  subtitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    flex: 1,
    marginBottom: "8px",
  }),
  pnoeBenefitsContainer: (theme: Theme) => ({
    height: "56px",
    backgroundColor: theme.colorTokens.N[30],
    padding: "8px 12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "row" as "row",
    alignItems: "center",
    justifyContent: "space-between",
  }),
  benefitLabel: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
  }),
  separator: {
    height: "16px",
  },
  addCustomBenefitContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  chipContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    rowGap: "8px",
    marginTop: "8px",
  },
  chipLabel: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.N[30],
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    borderRadius: "8px",
  }),
};
